import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { resumeSession, setAccessToken } from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setVendor(data.vendor || null);
    } catch (e) {
      setAccessToken(null);
      setUser(null);
      setVendor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-read the signed-in account (after profile changes).
  const refresh = loadMe;

  // On page load: resume the session from the secure cookie (or move an
  // older sign-in over to a session), then load the account.
  useEffect(() => {
    (async () => {
      if (await resumeSession()) await loadMe();
      else setLoading(false);
    })();
  }, [loadMe]);

  // G3: api.js signals an expired/revoked session. Clearing the user makes
  // ProtectedRoute redirect to the login page (with ?next= back here).
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setVendor(null);
      try { sessionStorage.setItem("pm_session_expired", "1"); } catch (e) { /* storage unavailable */ }
    };
    window.addEventListener("pm:session-expired", onExpired);
    return () => window.removeEventListener("pm:session-expired", onExpired);
  }, []);

  const loginWith = useCallback((token, u, v = null) => {
    setAccessToken(token);
    setUser(u);
    setVendor(v);
  }, []);

  const logout = useCallback(async () => {
    // Signing out ends every session: all three sites and every device.
    try { await api.post("/auth/logout-all"); } catch (e) { logger.warn("Logout API call failed (clearing local state anyway):", e); }
    setAccessToken(null);
    setUser(null);
    setVendor(null);
    // G4: a shared device shouldn't show the next person this user's cart.
    window.dispatchEvent(new Event("pm:logout"));
  }, []);

  const value = useMemo(
    () => ({ user, vendor, loading, loginWith, logout, refresh }),
    [user, vendor, loading, loginWith, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
